import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { clearToken, getToken, setToken } from './api';
import { authService } from './api-service';
import type { User } from './types';
import { TermsAcceptanceModal } from '../components/TermsAcceptanceModal';

interface AuthContextValue {
  user: User | null;
  loading: boolean;
  login: (username: string, password: string) => Promise<void>;
  loginWithGoogle: (credential: string) => Promise<void>;
  loginWithFacebook: (accessToken: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [showTerms, setShowTerms] = useState(false);

  useEffect(() => {
    const token = getToken();
    if (!token) {
      setLoading(false);
      return;
    }
    authService
      .me()
      .then((u) => {
        setUser(u);
        if (u && !u.is_staff && !u.has_accepted_terms) {
          setShowTerms(true);
        }
      })
      .catch(() => {
        clearToken();
      })
      .finally(() => setLoading(false));
  }, []);

  async function handleLogin(data: { token: string; user: User }) {
    setToken(data.token);
    setUser(data.user);
    if (!data.user.is_staff && !data.user.has_accepted_terms) {
      setShowTerms(true);
    }
  }

  async function login(username: string, password: string) {
    const data = await authService.login(username, password);
    await handleLogin(data);
  }

  async function loginWithGoogle(credential: string) {
    const data = await authService.googleLogin(credential);
    await handleLogin(data);
  }

  async function loginWithFacebook(accessToken: string) {
    const data = await authService.facebookLogin(accessToken);
    await handleLogin(data);
  }

  async function logout() {
    try {
      await authService.logout();
    } catch {
      // Token may already be invalid
    }
    clearToken();
    setUser(null);
    setShowTerms(false);
    window.location.href = '/';
  }

  async function refreshUser() {
    try {
      const u = await authService.me();
      setUser(u);
    } catch {
      // Ignore
    }
  }

  function handleTermsAccepted() {
    setShowTerms(false);
    if (user) {
      setUser({ ...user, has_accepted_terms: true });
    }
  }

  return (
    <AuthContext.Provider value={{ user, loading, login, loginWithGoogle, loginWithFacebook, logout, refreshUser }}>
      {children}
      {showTerms && <TermsAcceptanceModal onAccepted={handleTermsAccepted} />}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
